if (!customElements.get("m-faq-page-nav")) {
  class MFaqPageNav extends HTMLElement {
    constructor() {
      super();
      this.rafId = null;
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onLinkClick = this.onLinkClick.bind(this);
      this.onMutate = this.onMutate.bind(this);
    }

    connectedCallback() {
      this.links = Array.from(this.querySelectorAll("[data-fpn-link]"));
      this.activeIndex = this.links.findIndex((link) => link.classList.contains("is-active"));
      if (this.activeIndex < 0) this.activeIndex = 0;

      this.links.forEach((link) => {
        link.addEventListener("click", this.onLinkClick);
        const target = this.getTarget(link);
        if (target && target.id) {
          link.setAttribute("href", `#${target.id}`);
        }
      });

      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);

      this.bodyObserver = new MutationObserver(this.onMutate);
      this.bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["class"],
      });

      this.sync();

      if (window.Shopify && Shopify.designMode) {
        this._onSectionLoad = (event) => {
          if (event.target && event.target.contains(this)) {
            this.sync();
          }
        };
        document.addEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this.links.forEach((link) => {
        link.removeEventListener("click", this.onLinkClick);
      });
      window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("resize", this.onResize);
      if (this.bodyObserver) this.bodyObserver.disconnect();
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this._onSectionLoad) {
        document.removeEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.sync();
      });
    }

    onResize() {
      this.sync();
    }

    onMutate() {
      this.sync();
    }

    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    isHeaderVisible() {
      return document.body.classList.contains("header-scroll-up");
    }

    getHeaderHeight() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue("--m-header-height");
      const parsed = parseFloat(raw);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
      const header = document.querySelector(".m-header__wrapper");
      return header ? header.offsetHeight : 0;
    }

    getHeaderOffset() {
      let offset = this.isHeaderVisible() ? this.getHeaderHeight() : 0;
      if (document.documentElement.classList.contains("announcement-is-stuck")) {
        const raw = getComputedStyle(document.documentElement).getPropertyValue("--m-announcement-height");
        const parsed = parseFloat(raw);
        if (!Number.isNaN(parsed)) offset += parsed;
      }
      return offset;
    }

    getTarget(link) {
      const selector = link.getAttribute("data-fpn-target");
      if (!selector) return null;
      try {
        return document.querySelector(selector);
      } catch (error) {
        return null;
      }
    }

    sync() {
      const headerOffset = this.getHeaderOffset();
      document.documentElement.style.setProperty("--fpn-header-offset", `${headerOffset}px`);

      const lastTarget = this.getTarget(this.links[this.links.length - 1]);
      const pastContent = lastTarget && lastTarget.getBoundingClientRect().bottom < headerOffset + 48;
      this.classList.toggle("is-ended", Boolean(pastContent));

      this.updateActiveLink(headerOffset);
    }

    updateActiveLink(headerOffset) {
      const probeY = headerOffset + 96;
      let activeIndex = 0;

      this.links.forEach((link, index) => {
        const target = this.getTarget(link);
        if (!target) return;
        if (target.getBoundingClientRect().top <= probeY) {
          activeIndex = index;
        }
      });

      this.links.forEach((link, index) => {
        const isActive = index === activeIndex;
        link.classList.toggle("is-active", isActive);
        if (isActive) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });

      this.activeIndex = activeIndex;
    }

    onLinkClick(event) {
      const link = event.currentTarget;
      const target = this.getTarget(link);
      if (!target) return;

      event.preventDefault();

      const dest = target.getBoundingClientRect().top + window.scrollY;
      const goingUp = dest < window.scrollY - 2;
      const headerOffset = goingUp ? this.getHeaderHeight() : 0;
      const top = Math.max(dest - headerOffset - 16, 0);

      window.scrollTo({
        top,
        behavior: this.prefersReducedMotion() ? "auto" : "smooth",
      });
    }
  }

  customElements.define("m-faq-page-nav", MFaqPageNav);
}
